/**
 * AUTHORIZED IS NOT EXECUTED.
 *
 * This module exists because of one sentence in the platform's own autonomy
 * policy, verified against source before a line of this was written:
 *
 *   Approving a schedule proposal records the decision and moves no date.
 *   `decide_proposal` sets `applied = False` UNCONDITIONALLY.
 *
 * The backend goes to real trouble here. It returns `applied: false`, an
 * applied count of zero, and a `deferredWrites` list naming exactly which
 * tables it did not write and what is blocking each one. That list is the
 * platform stating what it deliberately did NOT do — an abstention, and a
 * feature rather than an error.
 *
 * Until this module, the frontend dropped all of it and showed a green
 * success toast. The toast text was not a lie, but a green toast that
 * auto-dismisses is how "your decision was recorded and the schedule did not
 * move" becomes "done" in a reader's head.
 *
 * Three rules, each with a test:
 *
 *  1. **THE OUTCOME IS READ, NEVER ASSUMED.** `applied` comes off the
 *     payload. If a response ever arrives with `applied: true`, this renders
 *     an applied outcome — and if the key is missing entirely, it renders
 *     neither, because guessing in either direction is worse than saying the
 *     server did not state it.
 *  2. **AN APPROVED COUNT IS NOT AN APPLIED COUNT.** The precon commit
 *     returns `approvedActivityCount`, which counts what the analysis
 *     covered — not what changed. Beside `applied: false` it reads as
 *     "12 activities changed" unless it is labelled as what it is.
 *  3. **THERE IS NO RUNTIME AUTONOMY MODE.** The five levels are policy
 *     vocabulary mapped onto controls that exist. The policy is explicit:
 *     there is no `autonomy_level` column anywhere in the schema, and the
 *     levels are not a value the code reads and branches on. So nothing here
 *     renders a per-project mode badge — that would be a runtime fact this
 *     platform does not have.
 */

/**
 * The contracts this module reads. Both are merged; neither is autonomy
 * orchestration, and this module invents no third shape.
 */
export const DECISION_CONTRACTS = ['sa-phase-2', 'precon-control-plane-1'];

/* -------------------------------------------------------------------------
 * THE OUTCOME OF A HUMAN DECISION
 * ---------------------------------------------------------------------- */

const OUTCOMES = {
  RECORDED_NOT_APPLIED: {
    key: 'RECORDED_NOT_APPLIED',
    // Deliberately two clauses. The first is what happened; the second is
    // what did not, and it is the half a reader skips if it is not said.
    label: 'Decision recorded — nothing was changed',
    tone: 'recorded',
    meaning:
      'Your decision is on the record and audited. No planned date, activity, package or procurement line was written.',
  },
  RECORDED_AND_APPLIED: {
    key: 'RECORDED_AND_APPLIED',
    label: 'Decision recorded and applied',
    tone: 'applied',
    meaning: 'The server states that this decision was written through to the schedule.',
  },
  NOT_STATED: {
    key: 'NOT_STATED',
    label: 'The server did not say whether anything was changed',
    tone: 'unknown',
    meaning:
      'This response carries no applied flag. Nothing has been assumed in either direction — check the record before acting on it.',
  },
};

export const outcomeDisplay = (key) => OUTCOMES[key] || OUTCOMES.NOT_STATED;

export const APPROVED_COUNT_CAVEAT =
  'This counts what the analysis covered, not what changed. Nothing was written.';

export const DEFERRED_WRITES_LEAD =
  'What OMNΛS did not write, and what is blocking each one. This is the platform stating its own limits — not a failure of your decision.';

/** One deferred write: a target, what it would have done, and its blocker. */
export const presentDeferredWrite = (entry) => {
  if (!entry) return null;
  return {
    target: entry.target || null,
    description: entry.description || null,
    blockedBy: entry.blockedBy || null,
  };
};

/**
 * The outcome of a decision, read from whichever of the two shapes arrived.
 *
 * `applied` is the discriminator and it is read, never inferred from the
 * presence of a message or a 200. A missing key yields NOT_STATED rather
 * than a cheerful default.
 */
export const presentDecisionOutcome = (response) => {
  if (!response) return null;
  const stated = Object.prototype.hasOwnProperty.call(response, 'applied');
  const applied = response.applied === true;
  const outcome = !stated
    ? OUTCOMES.NOT_STATED
    : applied
      ? OUTCOMES.RECORDED_AND_APPLIED
      : OUTCOMES.RECORDED_NOT_APPLIED;
  const deferred = (response.deferredWrites || []).map(presentDeferredWrite).filter(Boolean);
  // The scheduling-agent shape states what it APPLIED (zero). The precon
  // shape states what it APPROVED, which is a different number entirely and
  // must never be shown as a change count.
  const appliedCount = Object.prototype.hasOwnProperty.call(response, 'appliedActivityCount')
    ? response.appliedActivityCount
    : null;
  const approvedActivityCount = Object.prototype.hasOwnProperty.call(
    response,
    'approvedActivityCount'
  )
    ? response.approvedActivityCount
    : null;
  const approvedPackageCount = Object.prototype.hasOwnProperty.call(
    response,
    'approvedPackageCount'
  )
    ? response.approvedPackageCount
    : null;
  return {
    ...outcome,
    applied,
    appliedStated: stated,
    // The server's own sentence, kept verbatim. It usually says more
    // precisely what was not touched than any wording of ours would.
    message: response.message || null,
    proposalId: response.proposalId || null,
    state: response.state || response.status || null,
    decidedAt: response.decidedAt || response.approvalRecordedAt || null,
    decidedBy: response.decidedByUserId || response.approvedByUserId || null,
    appliedCount,
    approvedActivityCount,
    approvedPackageCount,
    // Only meaningful where an approved count sits beside applied: false.
    approvedCountCaveat:
      !applied && (approvedActivityCount !== null || approvedPackageCount !== null)
        ? APPROVED_COUNT_CAVEAT
        : null,
    requiresEitlApproval: response.requires_eitl_approval === true,
    deferredWrites: deferred,
    deferredWritesLead: deferred.length ? DEFERRED_WRITES_LEAD : null,
    // A decision that changed nothing AND names nothing deferred is worth
    // saying out loud rather than rendering as a blank section.
    silentlyUnchanged: !applied && stated && deferred.length === 0,
  };
};

/* -------------------------------------------------------------------------
 * THE FIVE AUTONOMY LEVELS — policy vocabulary, not a runtime value
 * ---------------------------------------------------------------------- */

export const NO_RUNTIME_MODE_NOTE =
  'These levels are policy vocabulary mapped onto controls that exist in code. There is no autonomy-level field anywhere in this platform’s schema and nothing reads one at runtime, so this describes what OMNΛS is permitted to do — never what mode a project is in.';

/**
 * The five, with the enforcement status the policy states and source
 * confirms. `enforcement` is deliberately not a boolean: level 3 is enforced
 * for eight guarded tools and explicitly blocked for schedule application,
 * and flattening that to "yes" would overstate it.
 */
const LEVELS = [
  {
    level: 0,
    name: 'OBSERVE',
    label: 'Reads and reports',
    summary: 'Computes nothing that could be mistaken for a recommendation, and proposes no change.',
    enforcement: 'ENFORCED',
    enforcementDetail: 'Read tools are a disjoint registry and cannot mutate.',
  },
  {
    level: 1,
    name: 'ADVISE',
    label: 'Analyses and names recommendations',
    summary: 'Writes nothing at all, not even a proposal record. A person reads the output and acts, or does not.',
    enforcement: 'ENFORCED',
    enforcementDetail: 'The analysis services are pure over already-read rows and open no transaction.',
  },
  {
    level: 2,
    name: 'PREPARE',
    label: 'Produces a reviewable proposal and stops',
    summary:
      'The proposal never becomes the plan by default, and never by the passage of time.',
    enforcement: 'ENFORCED',
    enforcementDetail:
      'Proposals default to PROPOSED and to requiring an operator confirmation; approval is a separate, explicitly confirmed call.',
  },
  {
    level: 3,
    name: 'SUPERVISED EXECUTION',
    label: 'Acts only after a person confirms that specific change',
    summary:
      'The person sees exactly what will happen before it happens, and the confirmation is stored and audited.',
    enforcement: 'PARTIAL',
    enforcementDetail:
      'Enforced for the eight guarded operator tools. For schedule application it is not merely gated but deferred: approving a proposal records the decision and writes no date.',
  },
  {
    level: 4,
    name: 'POLICY-BOUNDED AUTONOMY',
    label: 'Acts inside a written policy envelope',
    summary:
      'Would require an enumerated action set, hard bounds, a blast-radius limit, an audit record per action, and a kill switch.',
    enforcement: 'NOT_IMPLEMENTED',
    enforcementDetail:
      'Nothing in this platform runs at this level. There is no policy table, no bounds configuration, and no envelope in code.',
  },
];

export const AUTONOMY_LEVELS = LEVELS;

const ENFORCEMENT_DISPLAY = {
  ENFORCED: { key: 'ENFORCED', label: 'Enforced in code', tone: 'enforced' },
  PARTIAL: { key: 'PARTIAL', label: 'Partly enforced', tone: 'partial' },
  NOT_IMPLEMENTED: { key: 'NOT_IMPLEMENTED', label: 'Not implemented', tone: 'absent' },
};

export const enforcementDisplay = (status) =>
  ENFORCEMENT_DISPLAY[status] || { key: String(status ?? 'UNKNOWN'), label: 'Not stated', tone: 'absent' };

export const presentAutonomyLevels = () =>
  LEVELS.map((level) => ({ ...level, enforcementDisplay: enforcementDisplay(level.enforcement) }));

/** The highest level this platform actually enforces anywhere. */
export const highestEnforcedLevel = () =>
  LEVELS.filter((level) => level.enforcement === 'ENFORCED').reduce(
    (best, level) => (level.level > best.level ? level : best),
    LEVELS[0]
  );

/* -------------------------------------------------------------------------
 * COPY, AND THE GUARD THAT WALKS IT
 * ---------------------------------------------------------------------- */

export const AUTONOMY_COPY = {
  outcomeTitle: 'What your decision did',
  deferredTitle: 'What was not written',
  levelsTitle: 'What OMNΛS is permitted to do',
  dismiss: 'Dismiss',
  unchangedNote:
    'The server states nothing was changed and names nothing it deferred. Check the record before assuming a write happened elsewhere.',
};

/**
 * Words that would turn a recorded decision into a completed one. The whole
 * point of this surface is that those are different things.
 */
export const FORBIDDEN_AUTONOMY_WORDS = {
  completion: ['executed', 'completed', 'done', 'finished', 'success'],
  automation: ['full auto', 'autopilot', 'automatically applied', 'hands off'],
};

export const authoredAutonomyCopy = () => [
  ...Object.values(AUTONOMY_COPY),
  ...Object.values(OUTCOMES).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(ENFORCEMENT_DISPLAY).map((entry) => entry.label),
  ...LEVELS.flatMap((level) => [level.label, level.summary, level.enforcementDetail]),
  APPROVED_COUNT_CAVEAT,
  DEFERRED_WRITES_LEAD,
  NO_RUNTIME_MODE_NOTE,
];

export const findForbiddenAutonomyWords = (text) => {
  if (typeof text !== 'string') return [];
  const hits = [];
  Object.values(FORBIDDEN_AUTONOMY_WORDS).forEach((group) => {
    group.forEach((word) => {
      const pattern = new RegExp(`\\b${word.replace(/ /g, '\\s+')}\\b`, 'i');
      if (pattern.test(text)) hits.push(word);
    });
  });
  return hits;
};
